// Seq送信クライアント
// フロントエンドからSeqサーバーにログを送信
// サーバー側の設定に従って全ユーザーに適用される

// Cookie名（cookieManager.jsと同じ）
const COOKIE_USER_EMAIL = 'user_email';

// API URL
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL_V1 || '';

// サーバー設定のキャッシュ（1分間有効）
let serverConfigCache = null;
let serverConfigFetchedAt = 0;
const CONFIG_CACHE_DURATION = 60 * 1000; // 1分

// 環境判定（dev or prod）
function getEnvironment() {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
  // URLに'katsuki-dev'が含まれていれば開発環境
  if (apiUrl.includes('katsuki-dev')) {
    return 'dev';
  }
  // NODE_ENVでも判定
  if (process.env.NODE_ENV === 'development') {
    return 'dev';
  }
  return 'prod';
}

// 環境プレフィックス（[DEV] or [PROD]）
function getEnvironmentPrefix() {
  return getEnvironment() === 'dev' ? '[DEV]' : '[PROD]';
}

// Cookieからユーザーメールアドレスを取得するヘルパー
function getUserEmailFromCookie() {
  if (typeof document === 'undefined') return null;

  const nameEQ = COOKIE_USER_EMAIL + '=';
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);

    if (c.indexOf(nameEQ) === 0) {
      const encodedValue = c.substring(nameEQ.length, c.length);
      try {
        return decodeURIComponent(encodedValue);
      } catch {
        return encodedValue;
      }
    }
  }
  return null;
}

// 日本時間（JST）を取得するヘルパー
function getJapanTime() {
  const now = new Date();
  // 日本時間（UTC+9）
  const jstOffset = 9 * 60; // 分単位
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
  const jstTime = new Date(utcTime + (jstOffset * 60000));

  // YYYY-MM-DD HH:MM:SS 形式
  const year = jstTime.getFullYear();
  const month = String(jstTime.getMonth() + 1).padStart(2, '0');
  const day = String(jstTime.getDate()).padStart(2, '0');
  const hours = String(jstTime.getHours()).padStart(2, '0');
  const minutes = String(jstTime.getMinutes()).padStart(2, '0');
  const seconds = String(jstTime.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} JST`;
}


// sessionStorage/localStorageからアプリ名・コース名を取得するヘルパー
function getAppContext() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return { app_name: null, course_name: null };
  }
  
  try {
    const appName = sessionStorage.getItem('app_name') || null;
    const appTitle = sessionStorage.getItem('app_title') || null;
    let courseName = null;
    
    // localStorageからコース設定を取得
    if (appName) {
      const settingsKey = `${appName.toLowerCase()}_settings`;
      const settings = localStorage.getItem(settingsKey);
      if (settings) {
        try {
          const parsed = JSON.parse(settings);
          // courseDetail.titleがあればそれを、なければcourseを使う
          if (parsed.courseDetail && parsed.courseDetail.title) {
            courseName = parsed.courseDetail.title;
          } else if (parsed.course) {
            courseName = parsed.course;
          }
        } catch (e) {
          // パースエラーは無視
        }
      }
    }
    
    return { app_name: appName, app_title: appTitle, course_name: courseName };
  } catch (e) {
    return { app_name: null, course_name: null };
  }
}

class SeqClient {
  constructor() {
    this.serverUrl = process.env.NEXT_PUBLIC_SEQ_SERVER_URL;
    this.apiKey = process.env.NEXT_PUBLIC_SEQ_API_KEY;

    if (!this.serverUrl) {
      console.warn('[SEQ] NEXT_PUBLIC_SEQ_SERVER_URL is not set');
    }

    // デバイス情報（一度だけ取得）
    this.deviceInfo = typeof window !== 'undefined' ? {
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      touchSupport: 'ontouchstart' in window,
      platform: navigator.platform,
    } : {};
  }

  // サーバーから設定を取得（キャッシュ付き）
  async fetchServerConfig() {
    const now = Date.now();

    // キャッシュが有効なら使用
    if (serverConfigCache && (now - serverConfigFetchedAt) < CONFIG_CACHE_DURATION) {
      return serverConfigCache;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/log-config`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        // APIレスポンスは { statusCode, data: {...} } 形式
        serverConfigCache = result.data || result;
        serverConfigFetchedAt = now;
        return serverConfigCache;
      }
    } catch (error) {
      console.error('[SEQ] Failed to fetch server config:', error.message);
    }

    // エラー時はデフォルト（無効）を返す
    return { enabled: false };
  }

  // サーバー設定で有効かどうかを判定（同期版 - キャッシュのみ参照）
  isEnabledSync() {
    if (typeof window === 'undefined') return false;
    if (!this.serverUrl) return false;

    // キャッシュがあればそれを使用
    if (serverConfigCache) {
      return serverConfigCache.enabled === true;
    }

    // キャッシュがない場合は無効として扱う（非同期でフェッチを開始）
    this.fetchServerConfig();
    return false;
  }

  // サーバー設定で有効かどうかを判定（非同期版）
  async isEnabled() {
    if (typeof window === 'undefined') return false;
    if (!this.serverUrl) return false;

    const config = await this.fetchServerConfig();
    return config.enabled === true;
  }

  // 現在の設定状態を取得（非同期）
  async getStatus() {
    const config = await this.fetchServerConfig();

    return {
      isEnabled: config.enabled === true,
      source: 'server',
      updatedAt: config.updatedAt,
      updatedBy: config.updatedBy,
      serverUrl: this.serverUrl,
      hasApiKey: !!this.apiKey,
    };
  }

  // Seqにログを送信
  async send(level, message, properties = {}) {
    // 同期版でまず判定（キャッシュがある場合）
    if (!this.isEnabledSync()) {
      // キャッシュがない場合は非同期で確認
      const enabled = await this.isEnabled();
      if (!enabled) return;
    }

    try {
      // ユーザーメールアドレスを取得（ログイン済みの場合）
      const userEmail = getUserEmailFromCookie();
      // 環境情報
      const env = getEnvironment();
      const envPrefix = getEnvironmentPrefix();

      // アプリ名・コース名を自動付与
      const appContext = getAppContext();

      const logEvent = {
        '@t': new Date().toISOString(),
        '@l': level,
        '@mt': `${envPrefix} ${message}`,  // メッセージに環境プレフィックスを追加
        ...properties,
        environment: env,  // 環境情報をプロパティとして追加
        app_name: appContext.app_name,  // アプリ名（例: smartenglish, movieenglish）
        app_title: appContext.app_title,  // アプリタイトル
        course_name: appContext.course_name,  // 選択中のコース名
        jst_time: getJapanTime(),  // 日本時間を追加
        user_email: userEmail || 'not_logged_in',  // ユーザーメールアドレスを追加
        device: this.deviceInfo,
        source: 'frontend',
      };

      const url = `${this.serverUrl}/api/events/raw?clef`;
      const headers = {
        'Content-Type': 'application/vnd.serilog.clef',
      };

      if (this.apiKey) {
        headers['X-Seq-ApiKey'] = this.apiKey;
      }

      // 非同期で送信（await しない）
      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(logEvent),
        mode: 'cors',
      }).catch(err => {
        // Seq送信失敗は無視（コンソールには出力）
        console.error('[SEQ] Failed to send log:', err.message);
      });

    } catch (error) {
      console.error('[SEQ] Error creating log event:', error);
    }
  }

  // 引数を文字列化（オブジェクトの場合は構造化）
  formatArgs(args) {
    if (args.length === 0) return { message: '', properties: {} };

    const firstArg = args[0];

    // 第一引数が文字列の場合
    if (typeof firstArg === 'string') {
      const message = firstArg;
      const properties = {};

      // 残りの引数をプロパティとして追加
      args.slice(1).forEach((arg, index) => {
        if (typeof arg === 'object' && arg !== null) {
          Object.assign(properties, arg);
        } else {
          properties[`arg${index + 1}`] = arg;
        }
      });

      return { message, properties };
    }

    // 第一引数がオブジェクトの場合
    if (typeof firstArg === 'object' && firstArg !== null) {
      return {
        message: 'Object log',
        properties: { data: firstArg, ...args.slice(1) }
      };
    }

    // その他の場合
    return {
      message: String(firstArg),
      properties: { args: args.slice(1) }
    };
  }
}

// シングルトンインスタンス
let seqClientInstance = null;

export const getSeqClient = () => {
  if (typeof window === 'undefined') return null;

  if (!seqClientInstance) {
    seqClientInstance = new SeqClient();
  }
  return seqClientInstance;
};

export default SeqClient;
